import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typography } from '@figure/frontlib-gui';
import { PageHeader as PageHeaderBase } from 'shared/components/Typography';
import { Vendors, Profile, Link } from 'shared/constants';
import {
  ProgressBar,
  ProgressList,
  LinkList,
  LinkSubmit,
  IncomePartiallyVerified,
  InstructionModal,
} from './';

const PageHeader = styled(PageHeaderBase)`
  margin-bottom: 20px;
`;

const Income = ({
  complete,
  firstName,
  counter,
  offerPackage,
  items,
  onPlaidDelete,
  openPlaidLink,
  plaidLinkLoaded,
  onPlaidRetry,
  onLink,
  error,
  onSubmit,
  onClose,
  personalLoan,
  isOpen,
  hasSeenIncomeOption,
}) => {
  const headerText = complete ? (
    <>
      Great work <span className="fs-mask">{firstName}</span>! We have successfully verified your
      income.
    </>
  ) : (
    `Select an option below to verify your income information.`
  );
  const subText = complete
    ? ''
    : 'We’ll help guide you through the process. Please be patient—verifications can take up to a minute.';

  const {
    incomeRequestRecords,
    incomeResultRecordUuids,
    statedTotalIncome,
    verifiedTotalIncome,
    disabledIncomeSources = [],
  } = offerPackage;

  const isIncomePartiallyVerified =
    offerPackage.subDecision === 'VERIFIED_OFFER' ||
    offerPackage.subDecision === 'VERIFIED_DTI_LIMITED';

  const isInHoldingState =
    personalLoan.status?.manualPaystubUpload &&
    !personalLoan.status?.manualPaystubTasksComplete &&
    personalLoan.manualPaystubs?.some((paystub) => !paystub.completed);
  const isSelfEmployed = personalLoan.incomeSources?.some(
    (source) => source.employmentType === Profile.SELF_EMPLOYED
  );

  return (
    <>
      <PageHeader>{headerText}</PageHeader>
      <Typography.SmallBody>{subText}</Typography.SmallBody>
      <ProgressBar
        name={`Income verified ${
          !complete && counter.total > 0
            ? `(${counter.complete}/${counter.total} accounts retrieved...)`
            : ''
        }`}
        currentValue={!complete ? verifiedTotalIncome : statedTotalIncome}
        totalValue={statedTotalIncome}
        toggleEnabled={!complete && counter.total > 0}
      >
        <ProgressList
          offerPackage={offerPackage}
          items={items}
          incomeRequests={incomeRequestRecords
            .filter(
              (income) =>
                income.vendor === Vendors.TRUEWORK ||
                (income.vendor === Vendors.POINTSERV &&
                  income.verificationMethod?.indexOf(Vendors.POINTSERV) > -1)
            )
            .map((income) => ({
              ...income,
              completed: incomeResultRecordUuids.includes(income.uuid),
            }))}
          handleRetry={async (item) => {
            if (item.loginRequired) await openPlaidLink(item, () => onPlaidRetry(item));
            if (item.retryable) onPlaidRetry(item);
          }}
          handleDelete={onPlaidDelete}
        />
      </ProgressBar>
      {!complete &&
        !isInHoldingState &&
        !isSelfEmployed &&
        !disabledIncomeSources.includes(Link.MANUAL_PAYSTUB) &&
        (isIncomePartiallyVerified || hasSeenIncomeOption) && (
          <IncomePartiallyVerified uuid={personalLoan.uuid} />
        )}

      {!complete && plaidLinkLoaded && (
        <LinkList
          offerPackage={offerPackage}
          personalLoan={personalLoan}
          handleClick={onLink}
          serverError={error}
          disabledIncomeSources={disabledIncomeSources}
        />
      )}

      <LinkSubmit offerPackage={offerPackage} handleSubmit={() => onSubmit(complete)} />
      <InstructionModal isOpen={isOpen} handleClose={onClose} />
    </>
  );
};

Income.propTypes = {
  complete: PropTypes.bool.isRequired,
  firstName: PropTypes.string.isRequired,
  counter: PropTypes.shape({
    complete: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  // TODO: Pull from types file
  offerPackage: PropTypes.object.isRequired,
  // TODO: pull from types files
  items: PropTypes.array.isRequired,
  onPlaidDelete: PropTypes.func.isRequired,
  onPlaidRetry: PropTypes.func.isRequired,
  openPlaidLink: PropTypes.func.isRequired,
  plaidLinkLoaded: PropTypes.bool.isRequired,
  onLink: PropTypes.func.isRequired,
  error: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // TODO: Pull from types file,
  personalLoan: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  hasSeenIncomeOption: PropTypes.bool.isRequired,
};

export default Income;
